import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import Modal from '../../components/Modal';
import ModalHeading from '../../components/Modal/ModalHeading';
import ModalForm from '../../components/Modal/ModalForm';
import InputsSection from '../../components/Modal/InputsSection';
import ModalButtons from '../../components/Modal/ModalButtons';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import styles from './IDSRules.module.css';

const FRONTEND_IDS_RULES_ERROR_CODES = {
  COMPANY_DISCOVERY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  RULES_FETCH_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  RULES_RESPONSE_INVALID: ErrorCodes.API_INTERNAL_ERROR,
  RULE_TOGGLE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
  RULE_DELETE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildIDSRulesSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const groupRulesBySource = (rules) => {
  const grouped = {};

  rules.forEach(rule => {
    const source = rule.table || 'Unknown';
    if (!grouped[source]) {
      grouped[source] = [];
    }
    grouped[source].push(rule);
  });

  const result = [];
  Object.keys(grouped).sort().forEach(source => {
    const sortedGroupRules = grouped[source].sort((a, b) => a.id - b.id);
    result.push(...sortedGroupRules);
  });

  return result;
};

const IDSRulesScreen = () => {
  const mounted = useRef(true);
  const cancelTokenSourceRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Get user role and company from Redux store
  const userLogin = useSelector((state) => state.userLogin);
  const isAdmin = userLogin?.userInfo?.session?.role === 'admin';
  const sessionCompany = userLogin?.userInfo?.session?.company;
  
  const [rules, setRules] = useState([]);
  const [displayRules, setDisplayRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRules, setTotalRules] = useState(0);
  const [userCompany, setUserCompany] = useState('');

  // Deep-link from the dashboard: ?q=<sid> pre-fills the search so the rule that
  // fired opens directly (the backend search matches the sid column for a number).
  const location = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q) { setSearchQuery(q); setSearchInputValue(q); setCurrentPage(1); }
  }, [location.search]);
  const [, setDebugInfo] = useState({
    apiResponses: [],
    errors: [],
    lastAction: '',
  });
  const [sourceFilter, setSourceFilter] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [initialCompanyDiscoveryDone, setInitialCompanyDiscoveryDone] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [supportErrorMessage, setSupportErrorMessage] = useState('');
  const [copiedSupportError, setCopiedSupportError] = useState(false);
  // Bulk enable/disable of every rule matching a substring (for the selected source).
  const [bulkMatch, setBulkMatch] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  // Deep-link ?company=<name> selects that company's ruleset as the source, so a
  // provider's IDS "Investigate" lands on the right company (not the default
  // first one). Runs once companies are discovered; best-effort name/table match.
  useEffect(() => {
    const c = new URLSearchParams(location.search).get('company');
    if (!c || !availableCompanies.length) return;
    const match = availableCompanies.find(
      (x) => x.idsTable === c || x.name === c || x.displayName === c ||
             x.idsTable === `${c}_IDS`);
    if (match && sourceFilter !== match.idsTable) setSourceFilter(match.idsTable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableCompanies, location.search]);

  const setIDSRulesSupportError = useCallback((errorCode, pageInfo, sourceMessage, fatal = false) => {
    setSupportErrorMessage(buildIDSRulesSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
    if (fatal) {
      setError(true);
    }
  }, []);

  const clearIDSRulesSupportError = useCallback(() => {
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);
  
  // Fetch the current user's company and discover available companies for FLC
  useEffect(() => {
    const performInitialDiscovery = async () => {
      if (!mounted.current) return;

      const currentCompany = sessionCompany || '';
      console.log('Setting userCompany from Redux to:', currentCompany);
      setUserCompany(currentCompany);

      setDebugInfo(prev => ({ ...prev, lastAction: 'Performing initial company discovery' }));

      // Always ask the backend. It reports `isProvider` directly, so provider
      // discovery no longer depends on a company literally named 'FLC' — the
      // provider company can be named anything (e.g. 'Frontline').
      await discoverAvailableCompaniesForFLC();
    };

    performInitialDiscovery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionCompany]);

  const discoverAvailableCompaniesForFLC = async () => {
    if (!mounted.current) return;
    console.log('FLC user detected, discovering available companies...');
    setDebugInfo(prev => ({ ...prev, lastAction: 'Discovering companies for FLC' }));
    try {
      // Fetch a minimal set of rules to discover table names (via categories)
      // limit: 1 is fine as we are interested in the 'categories' field which reflects all tables queried by backend for FLC.
      const { data } = await axios.get('/api/ids_rules', { params: { limit: 1 } }); // No source filter needed
      
      console.log('Company discovery response (/api/ids_rules):', data);
      setDebugInfo(prev => ({
        ...prev,
        apiResponses: [...prev.apiResponses, { endpoint: '/api/ids_rules (discovery)', timestamp: new Date().toISOString(), data: data }]
      }));

      if (data && !data.isProvider) {
        // Client account: the backend pins every query to the caller's own
        // company, so their own company is the only selectable source. Keep
        // userCompany as their real name (not the 'FLC' sentinel).
        const own = data.userCompany || sessionCompany || '';
        if (mounted.current && own) setUserCompany(own);
        setAvailableCompanies(own ? [{ name: own, idsTable: own, displayName: own }] : []);
        if (own && !sourceFilter) setSourceFilter(own);
        return;
      }

      // Provider account: promote to the internal 'FLC' sentinel the gates use,
      // regardless of the provider company's actual name (e.g. 'Frontline').
      if (mounted.current) setUserCompany('FLC');

      if (data && data.categories && data.categories.length > 0) { // Prioritize data.categories
        const companiesFromCategories = data.categories.map(catName => ({
          name: catName,
          idsTable: catName,
          displayName: catName
        })).sort((a, b) => a.displayName.localeCompare(b.displayName));

        console.log('Discovered companies from categories:', companiesFromCategories);
        setAvailableCompanies(companiesFromCategories);

        if (companiesFromCategories.length > 0) {
          // Set source filter to the first discovered company if not already set by user preference later
          if (!sourceFilter) {
            setSourceFilter(companiesFromCategories[0].idsTable);
          }
        }
      } else if (data && data.rules && data.rules.length > 0) { 
        // Fallback to old method if categories isn't present or empty but rules are
        console.warn('Company discovery: data.categories was empty or not present. Falling back to rule.table from rules.');
        const uniqueTables = [...new Set(data.rules.map(rule => rule.table || ''))].filter(Boolean);
        const companiesFromTables = uniqueTables.map(table => ({
          name: table.replace('_IDS', ''),
          idsTable: table,
          displayName: table.replace('_IDS', '')
        })).sort((a, b) => a.displayName.localeCompare(b.displayName));

        console.log('Discovered companies from rule.table (fallback):', companiesFromTables);
        setAvailableCompanies(companiesFromTables);
        if (companiesFromTables.length > 0 && !sourceFilter) {
          setSourceFilter(companiesFromTables[0].idsTable);
        }
      } else {
        console.warn('Company discovery: API returned no categories or rules with table info for discovery.', data);
        setAvailableCompanies([]); // Set to empty if no companies found
      }
    } catch (error) {
      console.error('Error discovering available companies:', error);
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors, { source: 'discoverAvailableCompaniesForFLC', timestamp: new Date().toISOString(), message: error.message, stack: error.stack }]
      }));
      if (mounted.current) {
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.COMPANY_DISCOVERY_FAILED,
          'IDS Rules Page / Company Discovery Failed',
          error?.message || String(error),
          false
        );
      }
    } finally {
      setInitialCompanyDiscoveryDone(true);
    }
  };

  const fetchRules = useCallback(async () => {
    if (!mounted.current || !initialCompanyDiscoveryDone) return; // Wait for initial discovery

    // If FLC and sourceFilter is not yet set, wait.
    if (userCompany === 'FLC' && !sourceFilter) {
        console.log('FLC user, but sourceFilter is not set. Waiting for sourceFilter.');
        return;
    }
    
    // Cancel any previous requests
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('New request initiated');
    }
    
    const params = {
      page: currentPage,
      limit: pageSize,
      category: categoryFilter !== 'all' ? categoryFilter : null,
      search: searchQuery || null
    };

    // Only set source param for FLC users who can choose.
    if (userCompany === 'FLC' && sourceFilter) {
      params.source = sourceFilter;
    } else if (userCompany !== 'FLC' && userCompany && sourceFilter) {
      // For non-FLC users, always use their derived sourceFilter
      params.source = sourceFilter;
    }
    
    console.log('Fetching rules with params:', params, 'User company:', userCompany, 'Source filter:', sourceFilter);
    
    setDebugInfo(prev => ({
      ...prev,
      lastAction: 'Fetching IDS rules'
    }));
    
    // Create a new cancel token source
    cancelTokenSourceRef.current = axios.CancelToken.source();
    
    try {
      setLoading(true);
      clearIDSRulesSupportError();
      
      // Include pagination and filter parameters
      const { data } = await axios.get('/api/ids_rules', {
        params,
        cancelToken: cancelTokenSourceRef.current.token
      });
      
      console.log('IDS rules response received:', data);
      
      setDebugInfo(prev => ({
        ...prev,
        apiResponses: [...prev.apiResponses, {
          endpoint: '/api/ids_rules',
          timestamp: new Date().toISOString(),
          params: params,
          data: data
        }]
      }));
      
      if (!mounted.current) return;
      
      if (data && data.rules) {
        const rules = data.rules;
        // Group rules by source table
        const groupedRules = groupRulesBySource(rules);
        
        setRules(groupedRules);
        setDisplayRules(groupedRules);
        
        // Update pagination information
        setTotalPages(data.pagination.total_pages);
        setTotalRules(data.pagination.total_rules);
        
        // Extract unique categories from rules
        // const uniqueCategories = [...new Set(data.rules.map(rule => rule.category))];
        // setCategories(uniqueCategories); // This might be redundant if API sends categories
        
        // Set available categories if provided by the API (this is distinct from company discovery)
        // Rule categories are currently returned by the API but not rendered as a separate filter.
        
        // THIS BLOCK SHOULD BE REMOVED as availableCompanies (for rule source dropdown) is now handled by discovery
        // if (userCompany === 'FLC' && availableCompanies.length === 0) {
        //   // Get unique table names from rules
        //   const uniqueTables = [...new Set(data.rules.map(rule => rule.table || ''))];
        //   const filteredTables = uniqueTables.filter(Boolean);
          
        //   if (filteredTables.length > 0) {
        //     console.log('Extracting companies from rules response:', filteredTables);
            
        //     // Create company objects from table names
        //     const companiesFromTables = filteredTables.map(table => ({
        //       name: table.replace('_IDS', ''),
        //       idsTable: table,
        //       displayName: table.replace('_IDS', '')
        //     }));
            
        //     setAvailableCompanies(companiesFromTables); // This is what we want to avoid here
        //     // If no source filter is set, set it to the first company
        //     if (!sourceFilter && companiesFromTables.length > 0 && !showAllCompanies) {
        //       setSourceFilter(companiesFromTables[0].idsTable);
        //     }
        //   }
        // }
        
        // Get unique table names if company is FLC (this might be for a different UI element or debugging)
        // if (userCompany === 'FLC') {
        //   const uniqueTables = [...new Set(data.rules.map(rule => rule.table || ''))];
        //   const filteredTables = uniqueTables.filter(Boolean);
        //   setTableOptions(filteredTables); // This seems to be for 'tableOptions' state, not 'availableCompanies'
        // }
      } else {
        console.warn('API returned unexpected data format:', data);
        setDebugInfo(prev => ({
          ...prev,
          errors: [...prev.errors, {
            source: 'fetchRules',
            timestamp: new Date().toISOString(),
            message: 'Unexpected API response format',
            data: data
          }]
        }));
        
        setRules([]);
        setDisplayRules([]);
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.RULES_RESPONSE_INVALID,
          'IDS Rules Page / Invalid Rules Response',
          data?.msg || 'Unexpected API response format',
          false
        );
      }
      
      if (mounted.current) {
        setLoading(false);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      
      console.error('Error fetching rules:', error);
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors, {
          source: 'fetchRules',
          timestamp: new Date().toISOString(),
          message: error.message,
          stack: error.stack
        }]
      }));
      
      if (mounted.current) {
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.RULES_FETCH_FAILED,
          'IDS Rules Page / Rules Fetch Failed',
          error?.message || String(error),
          true
        );
        setLoading(false);
      }
    }
  }, [currentPage, pageSize, categoryFilter, searchQuery, sourceFilter, userCompany, initialCompanyDiscoveryDone, clearIDSRulesSupportError, setIDSRulesSupportError]);

  useEffect(() => {
    if (initialCompanyDiscoveryDone) { // Only fetch rules after initial discovery is done
      fetchRules();
    }
    
    return () => {
      // Cancel ongoing fetch when effect is cleaned up
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Effect cleanup');
      }
    };
  }, [fetchRules, initialCompanyDiscoveryDone]);

  // Handle filters
  useEffect(() => {
    // Apply filters whenever they change
    if (!mounted.current) return;
    
    // This filtering seems to be for rule categories, not rule sources.
    // The displayRules should be based on what fetchRules returns after server-side filtering by source.
    let filtered = [...rules]; // Start with rules already (potentially) filtered by source from the server
    
    if (categoryFilter !== 'all') { // This 'categoryFilter' is for rule categories
      filtered = filtered.filter(rule => rule.category === categoryFilter);
    }
    
    // Search query should also apply to the rules potentially already filtered by source
    const query = searchQuery.toLowerCase();
    if (searchQuery) { // Apply search if query exists
        filtered = filtered.filter(rule => 
            (rule.rule && rule.rule.toLowerCase().includes(query)) || 
            (rule.category && rule.category.toLowerCase().includes(query))
        );
    }
    
    setDisplayRules(filtered);
  }, [rules, categoryFilter, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault(); // Prevent form submission
    setSearchQuery(searchInputValue);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Enable/disable every rule whose text contains `bulkMatch`, for the selected
  // Rule Source. Appliances refetch on their next policy poll.
  const handleBulkToggle = async (enabled) => {
    const match = bulkMatch.trim();
    if (!match) { setBulkMsg('Enter text to match, e.g. stream-event:'); return; }
    setBulkBusy(true); setBulkMsg('');
    try {
      const { data } = await axios.post('/api/ids_rules/bulk', {
        source: sourceFilter, match, enabled,
      });
      if (data && data.status) {
        setBulkMsg(data.msg || `${enabled ? 'Enabled' : 'Disabled'} ${data.affected} rule(s)`);
        fetchRules();
      } else {
        setBulkMsg((data && data.msg) || 'Bulk update failed.');
      }
    } catch (err) {
      setBulkMsg(err?.response?.data?.msg || err.message || 'Bulk update failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleRule = useCallback(async (index) => {
    // Only allow admins to toggle rules
    if (!mounted.current || !isAdmin) return;
    
    // Create a new cancel token for this specific request
    const source = axios.CancelToken.source();
    
    try {
      // Use the rule's file_position property to identify it in the file correctly
      const rule = displayRules[index];
      const ruleFilePosition = rule.file_position !== undefined ? rule.file_position : rules.indexOf(rule);
      const ruleTable = rule.table; // Get the table name to pass to backend
      
      // console.log(`🔄 Toggling rule ${ruleFilePosition} from table ${ruleTable}`);
      
      setDebugInfo(prev => ({
        ...prev,
        lastAction: `Toggling rule ${ruleFilePosition} from ${ruleTable}`
      }));
      
      await axios.post('/api/toggle_rule', {
        index: ruleFilePosition,
        table: ruleTable,
        template: rule.template || false,
      }, {
        cancelToken: source.token
      });
      
      // console.log('✅ Rule toggled successfully');
      
      if (!mounted.current) {
        source.cancel('Component unmounted');
        return;
      }
      
      // console.log('🔄 Refreshing rules after toggle');
      
      // Refresh rules after toggling
      const { data } = await axios.get('/api/ids_rules', {
        params: {
          page: currentPage,
          limit: pageSize,
          category: categoryFilter !== 'all' ? categoryFilter : null,
          search: searchQuery || null,
          source: sourceFilter
        },
        cancelToken: source.token
      });
      
      setDebugInfo(prev => ({
        ...prev,
        apiResponses: [...prev.apiResponses, {
          endpoint: '/api/ids_rules (refresh after toggle)',
          timestamp: new Date().toISOString(),
          data: data
        }]
      }));
      
      if (!mounted.current) {
        source.cancel('Component unmounted');
        return;
      }
      
      if (data && data.rules) {
        // console.log(`📊 Rules refreshed, received ${data.rules.length} rules`);
        setRules(data.rules);
        setDisplayRules(data.rules);
        clearIDSRulesSupportError();
        
        // Update pagination information
        setTotalPages(data.pagination.total_pages);
        setTotalRules(data.pagination.total_rules);
      } else {
        console.warn('⚠️ API returned unexpected data format after toggle:', data);
        setDebugInfo(prev => ({
          ...prev,
          errors: [...prev.errors, {
            source: 'toggleRule - refresh',
            timestamp: new Date().toISOString(),
            message: 'Unexpected API response format after toggle',
            data: data
          }]
        }));
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.RULES_RESPONSE_INVALID,
          'IDS Rules Page / Toggle Refresh Invalid Response',
          data?.msg || 'Unexpected API response format after toggle',
          false
        );
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        // console.log('📝 Toggle request canceled:', error.message);
        return;
      }
      
      console.error('❌ Error toggling rule:', error);
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors, {
          source: 'toggleRule',
          timestamp: new Date().toISOString(),
          message: error.message,
          stack: error.stack
        }]
      }));
      
      if (mounted.current) {
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.RULE_TOGGLE_FAILED,
          'IDS Rules Page / Toggle Rule Failed',
          error?.message || String(error),
          false
        );
      }
    }
  }, [rules, displayRules, categoryFilter, searchQuery, sourceFilter, currentPage, pageSize, isAdmin, clearIDSRulesSupportError, setIDSRulesSupportError]);

  const deleteRule = useCallback(async () => {
    // Only allow admins to delete rules
    if (!mounted.current || !isAdmin || !ruleToDelete) return;
    
    // Create a new cancel token for this specific request
    const source = axios.CancelToken.source();
    
    try {
      const { rule } = ruleToDelete;
      const ruleFilePosition = rule.file_position !== undefined ? rule.file_position : rules.indexOf(rule);
      const ruleTable = rule.table;
      
      setDebugInfo(prev => ({
        ...prev,
        lastAction: `Deleting rule ${ruleFilePosition} from ${ruleTable}`
      }));
      
      await axios.post('/api/delete_rule', {
        index: ruleFilePosition,
        table: ruleTable,
        template: rule.template || false,
      }, {
        cancelToken: source.token
      });
      
      if (!mounted.current) {
        source.cancel('Component unmounted');
        return;
      }
      
      // Close the modal
      setShowDeleteModal(false);
      setRuleToDelete(null);
      
      // Refresh rules after deletion
      const { data } = await axios.get('/api/ids_rules', {
        params: {
          page: currentPage,
          limit: pageSize,
          category: categoryFilter !== 'all' ? categoryFilter : null,
          search: searchQuery || null,
          source: sourceFilter
        },
        cancelToken: source.token
      });
      
      setDebugInfo(prev => ({
        ...prev,
        apiResponses: [...prev.apiResponses, {
          endpoint: '/api/ids_rules (refresh after delete)',
          timestamp: new Date().toISOString(),
          data: data
        }]
      }));
      
      if (!mounted.current) {
        source.cancel('Component unmounted');
        return;
      }
      
      if (data && data.rules) {
        const groupedRules = groupRulesBySource(data.rules);
        setRules(groupedRules);
        setDisplayRules(groupedRules);
        clearIDSRulesSupportError();
        
        // Update pagination information
        setTotalPages(data.pagination.total_pages);
        setTotalRules(data.pagination.total_rules);
      } else {
        console.warn('API returned unexpected data format after delete:', data);
        setDebugInfo(prev => ({
          ...prev,
          errors: [...prev.errors, {
            source: 'deleteRule - refresh',
            timestamp: new Date().toISOString(),
            message: 'Unexpected API response format after delete',
            data: data
          }]
        }));
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.RULES_RESPONSE_INVALID,
          'IDS Rules Page / Delete Refresh Invalid Response',
          data?.msg || 'Unexpected API response format after delete',
          false
        );
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      
      console.error('Error deleting rule:', error);
      
      setDebugInfo(prev => ({
        ...prev,
        errors: [...prev.errors, {
          source: 'deleteRule',
          timestamp: new Date().toISOString(),
          message: error.message,
          stack: error.stack
        }]
      }));
      
      if (mounted.current) {
        setIDSRulesSupportError(
          FRONTEND_IDS_RULES_ERROR_CODES.RULE_DELETE_FAILED,
          'IDS Rules Page / Delete Rule Failed',
          error?.message || String(error),
          false
        );
        setShowDeleteModal(false);
        setRuleToDelete(null);
      }
    }
  }, [rules, categoryFilter, searchQuery, sourceFilter, currentPage, pageSize, isAdmin, ruleToDelete, clearIDSRulesSupportError, setIDSRulesSupportError]);

  const resetFilters = useCallback(() => {
    if (!mounted.current) return;
    
    // Reset source filter logic
    if (userCompany === 'FLC' && availableCompanies.length > 0) {
      setSourceFilter(availableCompanies[0].idsTable); // Reset to the first discovered company for FLC
    } else if (userCompany && userCompany !== 'FLC') {
      setSourceFilter(userCompany); // Reset to own company for non-FLC
    } else {
      setSourceFilter(''); // Clear if no company context
    }
    
    setCategoryFilter('all'); // Reset rule category filter
    setSearchQuery('');
    setSearchInputValue('');
    setCurrentPage(1); // Reset pagination
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [availableCompanies, userCompany]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleKeyDown = (e) => {
    // Submit search on Enter key
    if (e.key === 'Enter') {
      handleSearch(e);
    }
  };

  const handleDeleteClick = (rule, index) => {
    setRuleToDelete({ rule, index });
    setShowDeleteModal(true);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setRuleToDelete(null);
  };

  if (loading) return <Loader />;
  if (error) {
    return (
      <>
        <Error />
        {supportErrorMessage && (
          <div className={styles.supportErrorCard}>
            <strong>Support Error:</strong>
            <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            <button
              type='button'
              className={styles.copySupportErrorButton}
              onClick={handleCopySupportError}
            >
              {copiedSupportError ? 'Copied' : 'Copy Error Details'}
            </button>
          </div>
        )}
      </>
    );
  }

  // Debug information before render
  console.log('Render debug info:', {
    userCompany,
    isFlc: userCompany === 'FLC',
    availableCompanies,
    hasCompanies: availableCompanies.length > 0,
    showDropdown: userCompany === 'FLC' && availableCompanies.length > 0
  });

  return (
    <Content headerTitle="IDS Rules" currentMenu="idsrules">
      <div className={styles.container}>
        <h2 className={styles.title}>IDS Rules Management</h2>
        {supportErrorMessage && (
          <div className={styles.supportErrorBanner}>
            <div className={styles.supportErrorBannerText}>
              <strong>Support Error:</strong>
              <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
            </div>
            <button
              type='button'
              className={styles.copySupportErrorButton}
              onClick={handleCopySupportError}
            >
              {copiedSupportError ? 'Copied' : 'Copy Error Details'}
            </button>
          </div>
        )}
        
        <div className={styles.filterContainer}>
          <div className={styles.searchGroup}>
            <label>Search:</label>
            <div className={styles.searchInputContainer}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search rules..."
                className={styles.searchInput}
              />
              <button 
                onClick={handleSearch} 
                className={styles.searchButton}
              >
                Search
              </button>
            </div>
          </div>
          
          {console.log('Dropdown render check:', {
            userCompany, 
            isFlc: userCompany === 'FLC', 
            availableCompaniesLength: availableCompanies.length,
            availableCompanies
          })}
          
          {userCompany === 'FLC' && (
            <div className={styles.filterGroup}>
              <label>Rule Source:</label>
              <select 
                value={sourceFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setSourceFilter(value);
                  setCurrentPage(1);
                }}
                className={styles.select}
                // disabled={availableCompanies.length === 0} // Enable even if loading, shows "Loading..."
              >
                {/* The "All Companies" option was previously handled by showAllCompanies state and is now removed */}
                {availableCompanies.length > 0 ? (
                  availableCompanies.map(company => (
                    <option key={company.idsTable} value={company.idsTable}>
                      {company.displayName}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Loading companies...</option>
                )}
              </select>
            </div>
          )}
          
          {userCompany === 'FLC' && (
            <button onClick={resetFilters} className={styles.resetButton}>
              Reset Filters
            </button>
          )}
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        margin: '4px 0 14px', padding: '10px 12px',
                        border: '1px solid var(--light-grey, #e5e7eb)', borderRadius: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--grey)' }}>
              Bulk{sourceFilter ? ` (${sourceFilter})` : ''}:
            </span>
            <input
              value={bulkMatch}
              onChange={(e) => setBulkMatch(e.target.value)}
              placeholder="text to match, e.g. stream-event:"
              style={{ flex: '1 1 220px', minWidth: 180, padding: '7px 10px',
                       border: '1px solid var(--grey2, #cbd5e1)', borderRadius: 6 }}
            />
            <button type="button" disabled={bulkBusy} onClick={() => handleBulkToggle(false)}
              style={{ padding: '7px 14px', border: 'none', borderRadius: 6,
                       background: '#b45309', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              {bulkBusy ? 'Working…' : 'Disable matching'}
            </button>
            <button type="button" disabled={bulkBusy} onClick={() => handleBulkToggle(true)}
              style={{ padding: '7px 14px', border: 'none', borderRadius: 6,
                       background: '#0f9d58', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
              Enable matching
            </button>
            {bulkMsg && <span style={{ fontSize: 13, color: 'var(--grey)' }}>{bulkMsg}</span>}
          </div>
        )}

        <div className={styles.statsLarge}>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Total Rules</div>
            <div className={styles.statValue}>{totalRules}</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Enabled</div>
            <div className={styles.statValue}>{rules.filter(r => r.enabled).length}</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Disabled</div>
            <div className={styles.statValue}>{rules.filter(r => !r.enabled).length}</div>
          </div>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Displayed</div>
            <div className={styles.statValue}>{displayRules.length}</div>
          </div>
        </div>
        
        <div className={styles.tableContainer}>
          {displayRules.length > 0 ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID</th>
                  {userCompany === 'FLC' && <th>Source</th>}
                  <th>Rule</th>
                  {isAdmin && <th>Enable/Disable</th>}
                  {!isAdmin && <th>Status</th>}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let currentGroup = null;
                  return displayRules.map((rule, index) => {
                    const sourceGroup = rule.table || 'Unknown';
                    const isNewGroup = sourceGroup !== currentGroup;
                    currentGroup = sourceGroup;
                    
                    return (
                      <React.Fragment key={`rule-${index}`}>
                        {/* Only show group headers for FLC users */}
                        {isNewGroup && userCompany === 'FLC' && (
                          <tr className={styles.groupHeader}>
                            <td colSpan={userCompany === 'FLC' ? 4 : 3} className={styles.groupHeaderCell}>
                              {sourceGroup.replace('_IDS', '')}
                            </td>
                          </tr>
                        )}
                        <tr className={rule.enabled ? styles.enabledRule : styles.disabledRule}>
                          <td>{rule.sid}</td>
                          {userCompany === 'FLC' && (
                            <td className={styles.sourceCell}>
                              {rule.table ? rule.table.replace('_IDS', '') : 'Unknown'}
                            </td>
                          )}
                          <td className={styles.ruleText}>{rule.rule}</td>
                          <td>
                            {isAdmin ? (
                              <div className={styles.actionCell}>
                                <label className={styles.toggle}>
                                  <input 
                                    type="checkbox"
                                    className={styles.toggleInput}
                                    checked={rule.enabled} 
                                    onChange={() => toggleRule(index)}
                                  />
                                  <span className={styles.toggleSlider}></span>
                                </label>
                                <button 
                                  className={styles.deleteButton}
                                  onClick={() => handleDeleteClick(rule, index)}
                                  title="Delete rule"
                                >
                                  🗑️
                                </button>
                              </div>
                            ) : (
                              <span className={rule.enabled ? styles.statusEnabled : styles.statusDisabled}>
                                {rule.enabled ? 'Enabled' : 'Disabled'}
                              </span>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  });
                })()}
              </tbody>
            </table>
          ) : (
            <div className={styles.noRules}>
              {rules.length > 0 ? 'No rules match the current filters' : 'No rules found'}
            </div>
          )}
        </div>
        
        <div className={styles.paginationControls}>
          <button 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(1)}
            className={styles.pageButton}
          >
            First
          </button>
          <button 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(currentPage - 1)}
            className={styles.pageButton}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {currentPage} of {totalPages}
          </span>
          <button 
            disabled={currentPage === totalPages} 
            onClick={() => handlePageChange(currentPage + 1)}
            className={styles.pageButton}
          >
            Next
          </button>
          <button 
            disabled={currentPage === totalPages} 
            onClick={() => handlePageChange(totalPages)}
            className={styles.pageButton}
          >
            Last
          </button>
          <select 
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className={styles.pageSizeSelect}
          >
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
            <option value={250}>250 per page</option>
            <option value={500}>500 per page</option>
          </select>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal}>
        <ModalHeading heading="Delete Rule" onClose={handleDeleteModalClose} />
        <ModalForm>
          <InputsSection>
            <p className='delete_text'>
              Are you sure you want to delete rule <span>{ruleToDelete?.rule?.id}</span>?
              <br />
              This action cannot be undone.
            </p>
          </InputsSection>
          <ModalButtons
            submitButtonLabel="Delete Rule"
            onSubmit={deleteRule}
            cancelButtonLabel="Cancel"
            onCancel={handleDeleteModalClose}
            style={{ justifyContent: 'flex-start' }}
          />
        </ModalForm>
      </Modal>
    </Content>
  );
};

export default IDSRulesScreen;
